import ComponentLoader from '@common/components/ComponentLoader';
import { getG4bMyPageProjectsUrl } from '@console/common/g4bMyPage';
import DrawerContainer from '@console/containers/project/DrawerContainer';
import FloatingButtonContainer from '@console/containers/project/FloatingButtonContainer';
import type {
  ProjectLayoutPropsMappedFromDispatch,
  ProjectLayoutPropsMappedFromState
} from '@console/containers/project/ProjectLayoutContainer';
import MenuIcon from '@mui/icons-material/Menu';
import type { Theme } from '@mui/material';
import { Chip, IconButton, Stack, Typography } from '@mui/material';
import type { StyleRules, WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router';

const styles = (theme: Theme): StyleRules => ({
  content: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: 200,
    marginBottom: 130
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: 0
  },
  cancelledChip: {
    borderRadius: '4px',
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.background.console
  },
  childrenContainer: {
    paddingBottom: theme.spacing(7)
  }
});

export type ProjectLayoutPathParams = {
  urlCode?: string;
};

type ProjectLayoutProps = ProjectLayoutPropsMappedFromState &
  ProjectLayoutPropsMappedFromDispatch &
  WithStyles<typeof styles>;

const ProjectLayout: React.FC<React.PropsWithChildren<ProjectLayoutProps>> = ({
  isDrawerOpen,
  isSampleProject,
  initializeSelectedProject,
  exitProjectDetail,
  selectedProjectDetail,
  openDrawer,
  children,
  isMypageProject,
  classes
}) => {
  const params = useParams<ProjectLayoutPathParams>();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (params.urlCode) {
      initializeSelectedProject(params.urlCode)
        .then(isMypageProjectForLocation => {
          if (isMypageProjectForLocation) {
            location.href = `${getG4bMyPageProjectsUrl()}/${params.urlCode}`;
          } else {
            setIsLoaded(true);
          }
        })
        .catch(() => setIsLoaded(true));
    }
  }, []);

  useEffect(
    () => () => {
      exitProjectDetail();
    },
    []
  );

  return (
    <>
      {!isLoaded && <ComponentLoader isLoading={true} />}
      <div
        className={clsx(classes.content, {
          [classes.contentShift]: !isDrawerOpen
        })}
      >
        {isLoaded ? <DrawerContainer /> : null}
        {!isDrawerOpen ? (
          <IconButton
            className={classes.menuIcon}
            onClick={openDrawer}
            size="large"
          >
            <MenuIcon />
          </IconButton>
        ) : null}
        {isLoaded ? (
          <Fragment>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                mt: 2,
                justifyContent: 'flex-start',
                alignItems: 'flex-start'
              }}
            >
              <Typography variant="h6">{selectedProjectDetail.name}</Typography>
              {selectedProjectDetail.isCancelled && (
                <Chip
                  label="キャンセル済"
                  size="small"
                  className={classes.cancelledChip}
                />
              )}
            </Stack>
            <div className={classes.childrenContainer}>{children}</div>
          </Fragment>
        ) : null}
        {isSampleProject || isMypageProject ? null : (
          <FloatingButtonContainer />
        )}
      </div>
    </>
  );
};

export default withStyles(styles)(ProjectLayout);
